// @flow strict
import React from 'react';
import { Link, withPrefix } from 'gatsby';
import classNames from 'classnames';
import Icon from '../Icon';
import ThemeToggle from '../ThemeToggle';
import { getContactHref, getIcon } from '../../utils';
import { useSiteMetadata } from '../../hooks';
import styles from './SiteSidebar.module.scss';

type Props = {
  // Lets the page own where the column sits — sticky, full height, whatever the
  // layout needs — while this component owns what it looks like inside.
  className?: string,
  // 'page' opts into the site's sidebar-and-content grid. The constellation
  // places its own column instead and leaves this off.
  layout?: 'bare' | 'page',
  // On a narrow screen the foot — theme, archive, copyright — moves below the
  // page's own content instead of sitting under the menu. Only the home page
  // wants this; see the modifier in the stylesheet.
  splitOnMobile?: boolean
};

const SiteSidebar = ({ className, layout = 'bare', splitOnMobile = false }: Props) => {
  const {
    author, copyright, menu, archiveMenu
  } = useSiteMetadata();

  return (
    <aside
      className={classNames(
        styles['siteSidebar'],
        { [styles['siteSidebar--page']]: layout === 'page' },
        { [styles['siteSidebar--split']]: splitOnMobile },
        className
      )}
    >
      <div className={styles['siteSidebar__head']}>
        <img
          className={styles['siteSidebar__photo']}
          src={withPrefix(author.photo)}
          width="88"
          height="88"
          alt={author.name}
        />
        <p className={styles['siteSidebar__name']}>{author.name}</p>
        <p className={styles['siteSidebar__tagline']}>{author.bio}</p>

        <nav className={styles['siteSidebar__nav']}>
          {menu.map((item) => (
            <Link
              key={item.path}
              className={styles['siteSidebar__nav-link']}
              activeClassName={styles['siteSidebar__nav-link--active']}
              to={item.path}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <ul className={styles['siteSidebar__contacts']}>
          {Object.keys(author.contacts).map((name) => (!author.contacts[name] ? null : (
            <li key={name} className={styles['siteSidebar__contact']}>
              <a
                className={styles['siteSidebar__contact-link']}
                href={getContactHref(name, author.contacts[name])}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Icon name={name} icon={getIcon(name)} />
              </a>
            </li>
          )))}
        </ul>
      </div>

      {/* Theme, archive and copyright. On the home page's narrow layout this
          group moves below the page's own content. */}
      <div className={styles['siteSidebar__foot']}>
        <div className={styles['siteSidebar__toggle']}><ThemeToggle variant="switch" /></div>

        {(archiveMenu || []).map((item) => (
          <Link key={item.path} className={styles['siteSidebar__archive-link']} to={item.path}>
            {item.label}
          </Link>
        ))}

        <p className={styles['siteSidebar__copyright']}>{copyright}</p>
      </div>
    </aside>
  );
};

export default SiteSidebar;
